import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { MapPin, Eye, Heart, Phone, Mail, ChevronLeft, ChevronRight, ArrowLeft, Check } from "lucide-react";
import api from "../lib/api";
import { useLang } from "../lib/i18n";
import { useAuth } from "../context/AuthContext";
import { formatPrice, catMeta, typeLabel, regionLabel, imgUrl, imgLabel, specLabel, specDisplay, amenityLabel, PLACEHOLDER_IMAGE } from "../lib/format";
import ListingCard from "../components/ListingCard";

export default function ListingDetail() {
  const { id } = useParams();
  const { t, lang } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [listing, setListing] = useState(null);
  const [similar, setSimilar] = useState([]);
  const [imgIdx, setImgIdx] = useState(0);
  const [loading, setLoading] = useState(true);
  const [favIds, setFavIds] = useState([]);

  useEffect(() => {
    setLoading(true);
    window.scrollTo(0, 0);
    api.get(`/listings/${id}`)
      .then((r) => {
        setListing(r.data);
        setImgIdx(0);
        return api.get("/listings", { params: { category: r.data.category, limit: 4 } });
      })
      .then((r) => setSimilar(r.data.filter((l) => l.id !== id).slice(0, 3)))
      .catch(() => navigate("/browse"))
      .finally(() => setLoading(false));
  }, [id, navigate]);

  useEffect(() => {
    if (user) api.get("/favorites/ids").then((r) => setFavIds(r.data)).catch(() => {});
  }, [user]);

  const toggleFav = useCallback(async (lid) => {
    if (!user) { navigate("/login"); return; }
    const res = await api.post(`/favorites/${lid}`);
    setFavIds((prev) => (res.data.favorited ? [...prev, lid] : prev.filter((x) => x !== lid)));
  }, [user, navigate]);

  if (loading || !listing) {
    return <div className="min-h-screen flex items-center justify-center"><div className="w-10 h-10 border-2 border-gold border-t-transparent rounded-full animate-spin" /></div>;
  }

  const meta = catMeta(listing.category);
  const Icon = meta.icon;
  const images = listing.images?.length ? listing.images : [PLACEHOLDER_IMAGE];
  const isRent = listing.listing_type === "rent" || listing.category === "rentals";
  const isFav = favIds.includes(listing.id);

  const waNumber = (listing.contact_whatsapp || listing.contact_phone || "").replace(/[^0-9]/g, "");
  const waText = encodeURIComponent(`${lang === "en" ? "Hello, I'm interested in your listing on Koila Holdings" : "Bonjour, je suis intéressé par votre annonce sur Koila Holdings"}: ${listing.title}`);

  return (
    <div className="max-w-[1400px] mx-auto px-5 md:px-10 pt-24 pb-16">
      <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-white/50 hover:text-white text-sm mb-6" data-testid="back-btn">
        <ArrowLeft size={16} /> {t("back")}
      </button>

      <div className="grid lg:grid-cols-3 gap-10">
        {/* Main */}
        <div className="lg:col-span-2">
          {/* Gallery */}
          <div className="relative aspect-[16/10] overflow-hidden bg-[#121212] border border-white/10">
            <img src={imgUrl(images[imgIdx])} alt={imgLabel(images[imgIdx]) || listing.title} className="w-full h-full object-cover" data-testid="gallery-main-image" />
            {imgLabel(images[imgIdx]) && (
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent px-4 py-3" data-testid="gallery-caption">
                <span className="text-white text-sm">{imgLabel(images[imgIdx])}</span>
              </div>
            )}
            {images.length > 1 && (
              <>
                <button onClick={() => setImgIdx((i) => (i - 1 + images.length) % images.length)} className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/60 border border-white/10 flex items-center justify-center hover:border-gold" data-testid="gallery-prev">
                  <ChevronLeft className="text-white" />
                </button>
                <button onClick={() => setImgIdx((i) => (i + 1) % images.length)} className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/60 border border-white/10 flex items-center justify-center hover:border-gold" data-testid="gallery-next">
                  <ChevronRight className="text-white" />
                </button>
                <span className="absolute bottom-3 right-3 bg-black/70 text-white text-xs px-2 py-1">{imgIdx + 1}/{images.length}</span>
              </>
            )}
            <div className="absolute top-4 left-4 flex items-center gap-2 bg-black/60 px-3 py-1.5 border border-white/10">
              <Icon size={14} className="text-gold" />
              <span className="text-xs uppercase tracking-wider text-white/90">{meta[lang]}</span>
            </div>
          </div>
          {images.length > 1 && (
            <div className="flex gap-3 mt-3 overflow-x-auto">
              {images.map((im, i) => (
                <button key={i} onClick={() => setImgIdx(i)} className={`w-24 h-20 shrink-0 overflow-hidden border ${i === imgIdx ? "border-gold" : "border-white/10"}`}>
                  <img src={imgUrl(im)} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}

          {listing.video && (
            <div className="mt-4" data-testid="listing-video">
              <video src={listing.video} controls className="w-full max-h-[480px] bg-black border border-white/10" />
            </div>
          )}

          <div className="mt-8">
            <span className="text-gold text-xs uppercase tracking-wider">{typeLabel(listing.listing_type, lang)}</span>
            <h1 className="font-display text-3xl md:text-4xl text-white mt-2 leading-tight">{listing.title}</h1>
            <div className="flex items-center gap-5 text-white/50 text-sm mt-3">
              <span className="flex items-center gap-1.5"><MapPin size={15} /> {listing.city}, {regionLabel(listing.region, lang)}</span>
              <span className="flex items-center gap-1.5"><Eye size={15} /> {listing.views || 0} {t("views")}</span>
            </div>

            <div className="mt-8">
              <h2 className="text-white/90 font-medium mb-3 uppercase text-sm tracking-wider">{t("description")}</h2>
              <p className="text-white/60 leading-relaxed whitespace-pre-line">{listing.description}</p>
            </div>

            {(listing.street || listing.town_village || listing.division || listing.subdivision || listing.gps_lat) && (
              <div className="mt-8">
                <h2 className="text-white/90 font-medium mb-4 uppercase text-sm tracking-wider">{t("location")}</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-px bg-white/10 border border-white/10">
                  {listing.street && <LocCell label={t("f_street")} value={listing.street} />}
                  {listing.town_village && <LocCell label={t("f_town")} value={listing.town_village} />}
                  {listing.subdivision && <LocCell label={t("f_subdivision")} value={listing.subdivision} />}
                  {listing.division && <LocCell label={t("f_division")} value={listing.division} />}
                  <LocCell label={t("region")} value={regionLabel(listing.region, lang)} />
                </div>
                {listing.gps_lat && listing.gps_lng && (
                  <div className="mt-4" data-testid="listing-map">
                    <iframe
                      title="map"
                      src={`https://www.google.com/maps?q=${listing.gps_lat},${listing.gps_lng}&z=15&output=embed`}
                      className="w-full h-72 border border-white/10"
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                    />
                    <a
                      href={`https://www.google.com/maps?q=${listing.gps_lat},${listing.gps_lng}`}
                      target="_blank" rel="noopener noreferrer"
                      data-testid="view-map-btn"
                      className="mt-3 inline-flex items-center gap-2 border border-white/15 text-white px-5 py-2.5 text-sm hover:border-gold transition-colors"
                    >
                      <MapPin size={16} className="text-gold" /> {t("view_map_full")}
                    </a>
                  </div>
                )}
              </div>
            )}

            {listing.specs && Object.keys(listing.specs).filter((k) => k !== "amenities").length > 0 && (
              <div className="mt-8">
                <h2 className="text-white/90 font-medium mb-4 uppercase text-sm tracking-wider">{t("details")}</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-px bg-white/10 border border-white/10">
                  {Object.entries(listing.specs).filter(([k]) => k !== "amenities").map(([k, v]) => (
                    <div key={k} className="bg-[#121212] p-4">
                      <p className="text-white/40 text-xs uppercase tracking-wider">{specLabel(k, lang)}</p>
                      <p className="text-white mt-1 font-medium">{specDisplay(k, v, lang)}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {Array.isArray(listing.specs?.amenities) && listing.specs.amenities.length > 0 && (
              <div className="mt-8" data-testid="amenities-section">
                <h2 className="text-white/90 font-medium mb-4 uppercase text-sm tracking-wider">{t("amenities_title")}</h2>
                <div className="flex flex-wrap gap-2">
                  {listing.specs.amenities.map((a) => (
                    <span key={a} className="flex items-center gap-1.5 border border-gold/40 text-white/90 text-sm px-3 py-1.5">
                      <Check size={14} className="text-gold" /> {amenityLabel(a, lang)}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="lg:col-span-1">
          <div className="lg:sticky lg:top-24 bg-[#121212] border border-white/10 p-6">
            <p className="text-white/40 text-xs uppercase tracking-wider">{lang === "en" ? "Price" : "Prix"}</p>
            <p className="text-gold font-display text-3xl mt-1">
              {formatPrice(listing.price)}{isRent && <span className="text-white/40 text-sm font-sans">/{lang === "en" ? "mo" : "mois"}</span>}
            </p>

            <button
              onClick={() => toggleFav(listing.id)}
              data-testid="detail-fav-btn"
              className="w-full mt-5 flex items-center justify-center gap-2 border border-white/15 py-2.5 text-sm text-white/80 hover:border-gold transition-colors"
            >
              <Heart size={16} className={isFav ? "fill-gold text-gold" : ""} /> {isFav ? (lang === "en" ? "Saved" : "Enregistré") : t("nav_favorites")}
            </button>

            <div className="border-t border-white/10 mt-6 pt-6">
              <p className="text-white/90 font-medium mb-1">{t("contact_seller")}</p>
              <div className="flex items-center gap-2 mb-4">
                <p className="text-white/50 text-sm">{listing.contact_name}</p>
                {listing.seller_type && (
                  <span className="text-[10px] uppercase tracking-wider text-gold border border-gold/40 px-2 py-0.5">
                    {listing.seller_type === "agent" ? t("seller_agent") : t("seller_landlord")}
                  </span>
                )}
              </div>

              <a
                href={`https://wa.me/${waNumber}?text=${waText}`}
                target="_blank" rel="noopener noreferrer"
                data-testid="contact-whatsapp-btn"
                className="w-full flex items-center justify-center gap-2 bg-[#25D366] text-white py-3 font-medium hover:bg-[#1DA851] transition-colors mb-3"
              >
                <WhatsAppIcon /> {t("whatsapp")}
              </a>
              <a href={`tel:${listing.contact_phone}`} data-testid="contact-call-btn" className="w-full flex items-center justify-center gap-2 bg-white/10 text-white py-3 hover:bg-white/20 transition-colors mb-3">
                <Phone size={18} /> {t("call")}
              </a>
              <a href={`mailto:${listing.contact_email}?subject=${encodeURIComponent(listing.title)}`} data-testid="contact-email-btn" className="w-full flex items-center justify-center gap-2 border border-white/15 text-white py-3 hover:border-gold transition-colors">
                <Mail size={18} /> {t("email")}
              </a>
            </div>
          </div>
        </div>
      </div>

      {similar.length > 0 && (
        <div className="mt-20">
          <h2 className="font-display text-2xl md:text-3xl text-white mb-8">{t("similar")}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {similar.map((l, i) => (
              <ListingCard key={l.id} listing={l} index={i} favorited={favIds.includes(l.id)} onToggleFav={toggleFav} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function LocCell({ label, value }) {
  return (
    <div className="bg-[#121212] p-4">
      <p className="text-white/40 text-xs uppercase tracking-wider">{label}</p>
      <p className="text-white mt-1 font-medium">{value}</p>
    </div>
  );
}

function WhatsAppIcon() {  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
      <path d="M12.04 2c-5.46 0-9.91 4.45-9.91 9.91 0 1.75.46 3.45 1.32 4.95L2 22l5.25-1.38c1.45.79 3.08 1.21 4.79 1.21 5.46 0 9.91-4.45 9.91-9.91S17.5 2 12.04 2zm5.8 14.12c-.24.68-1.42 1.31-1.95 1.36-.5.05-1.13.07-1.83-.11-.42-.13-.96-.31-1.65-.61-2.91-1.26-4.81-4.19-4.95-4.38-.15-.2-1.19-1.58-1.19-3.01s.75-2.14 1.02-2.43c.27-.29.58-.36.78-.36.19 0 .39 0 .56.01.18.01.42-.07.66.5.24.59.83 2.02.9 2.17.07.15.12.32.02.51-.09.2-.14.32-.28.49-.14.17-.29.38-.42.51-.14.14-.28.29-.12.57.16.27.71 1.17 1.52 1.9 1.04.93 1.92 1.22 2.2 1.36.27.14.43.12.59-.07.16-.2.68-.79.86-1.06.18-.27.36-.22.61-.13.24.09 1.55.73 1.82.86.27.14.45.2.51.31.07.12.07.66-.17 1.34z"/>
    </svg>
  );
}
